import type { GConstructor } from './APILibrary';
import APILibrary, { ClassExtender, GenerateResource } from './APILibrary';
import type { Base } from 'active-resource';
import { Collection } from 'active-resource';
import type { ProfileQuestionAnswerResource } from 'util/resources/ProfileQuestionAnswer';
import { GeographyResource } from 'util/resources/Geography';

export interface Option {
  value: string;
  redirectToProfileQuestionId?: string;
  singleSelection?: boolean;
  flowControl?: {
    addUnlistedProfileQuestionIds?: string[];
  };
}

/**
 * Relationship classes
 */
export class ProfileQuestionAttributes {
  category: string = null;
  description: string = null;
  id: string = null;
  inputType: string = null;
  label: string = null;
  labelSubtext: string = null;
  minInput: number = null;
  name: string = null;
  options?: Option[] = null;
  config: string = null;
  order: string = null;
  required: boolean = null;
  unlisted: boolean = null;
}

export class ProfileQuestionBelongsTo {
  geography: () => GeographyResource = () => new GeographyResource();
}
export class ProfileQuestionHasOne {}

export class ProfileQuestionHasMany {
  profileQuestionAnswers: () => Collection<ProfileQuestionAnswerResource> = () =>
    new Collection<ProfileQuestionAnswerResource>();
}

/**
 * Utility interfaces
 */
export interface ProfileQuestionMethods {}

export interface ProfileQuestionInterface
  extends ProfileQuestionAttributes,
    ProfileQuestionBelongsTo,
    ProfileQuestionHasMany,
    ProfileQuestionType {}

type ProfileQuestionType = GConstructor<ProfileQuestionInterface>;

export interface ProfileQuestionResource
  extends Base<ProfileQuestionAttributes>,
    ProfileQuestionInterface,
    ProfileQuestionMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<ProfileQuestionAttributes>({
    queryName: 'profile_questions',
    className: 'ProfileQuestion',
    AttributesClass: ProfileQuestionAttributes,
    BelongsToClasses: [
      {
        Klass: ProfileQuestionBelongsTo,
      },
    ],
    HasManyClasses: [
      {
        Klass: ProfileQuestionHasMany,
      },
    ],
  });

export const ProfileQuestionResourceClass = <TBase extends ProfileQuestionType>(
  BaseConstructor: TBase,
): TBase => class extends BaseConstructor implements ProfileQuestionMethods {};

export class ProfileQuestionResource extends ClassExtender<ProfileQuestionInterface>(
  BaseClass,
  ProfileQuestionResourceClass,
) {}

/**
 * Resource instance
 */
export const ProfileQuestion =
  APILibrary.createResource<ProfileQuestionResource>(ProfileQuestionResource);
